/**
 * product.operations.ts — the PRODUCT document registry (CU-1b products third;
 * fullsuite plan §B2.4'/§B2.4b'/§B2.7'; taxonomy + pricing precedent).
 *
 * Document TEXTS are single-sourced from the contract pack
 * (`packages/contracts/operations/*.graphql`) via vite `?raw`, so the facade
 * can never drift from the reviewed text, and every one is machine-validated
 * against the frozen SDL snapshot by `scripts/validate-operations.mjs`.
 *
 * WHY `ProductWire*` AND NOT `ProductListPage`/`ProductDetailPage`.
 * Those two are B0.7' ergonomics fixtures for the future PAGE — a list-row
 * projection and a detail projection, both deliberately narrow. The FACADE
 * has to rebuild the WHOLE legacy product: `ProductListResponse.data` is
 * `Product[]`, and the mock's list rows are exactly as complete as its detail
 * row. Adopting the narrow page document for the list would have shipped a
 * lossy read under an unchanged signature — the one thing the CU rail exists
 * to prevent, and the same class of defect B2.4b'-read just closed in the SDL.
 * The two page documents stay what they were declared to be.
 *
 * ONE FRAGMENT, SIX DOCUMENTS. `ProductWireFields` is duplicated into each
 * document because the validator (and the persisted-operation model) treats
 * every file as self-contained. Duplication that nobody checks is drift
 * waiting to happen, so `productSeam.test.ts` asserts the fragment text is
 * BYTE-IDENTICAL across all five documents that carry it.
 *
 * INTERIM (masters/taxonomy/pricing precedent; removal trigger: product
 * adoption in codegen.ts): the types below are hand-declared rather than
 * generated `TypedDocumentNode` re-exports, and ride plain full-text POSTs.
 *
 * ID CANON: the graph exposes BOTH the opaque relay `id` and the raw
 * `productId`. The legacy wire reads the RAW one everywhere — routes, SO/PO
 * lines, every satellite — so `product.gql-map.ts` maps `productId` onto
 * `Product.id` and the opaque id is never handed to a consumer. This is the
 * CU-1a detail-404 lesson, applied before the flip instead of after it.
 */

import productWireListText from '../../../packages/contracts/operations/ProductWireList.graphql?raw';
import productWireDetailText from '../../../packages/contracts/operations/ProductWireDetail.graphql?raw';
import productWireCreateText from '../../../packages/contracts/operations/ProductWireCreate.graphql?raw';
import productWireUpdateText from '../../../packages/contracts/operations/ProductWireUpdate.graphql?raw';
import productWireSetActiveText from '../../../packages/contracts/operations/ProductWireSetActive.graphql?raw';
import productWireSkuAvailabilityText from '../../../packages/contracts/operations/ProductWireSkuAvailability.graphql?raw';

/** Operation names — must equal each file's basename (safelist identity). */
export const PRODUCT_OPERATION_NAMES = {
  wireList: 'ProductWireList',
  wireDetail: 'ProductWireDetail',
  wireCreate: 'ProductWireCreate',
  wireUpdate: 'ProductWireUpdate',
  wireSetActive: 'ProductWireSetActive',
  skuAvailability: 'ProductWireSkuAvailability',
} as const;

export const productOperations = {
  wireList: productWireListText,
  wireDetail: productWireDetailText,
  wireCreate: productWireCreateText,
  wireUpdate: productWireUpdateText,
  wireSetActive: productWireSetActiveText,
  skuAvailability: productWireSkuAvailabilityText,
} as const;

/** The five documents that carry `ProductWireFields` (the seam vitest pins them equal). */
export const PRODUCT_WIRE_FRAGMENT_DOCUMENTS = [
  productWireListText,
  productWireDetailText,
  productWireCreateText,
  productWireUpdateText,
  productWireSetActiveText,
] as const;

// ---------------------------------------------------------------------------
// Graph shapes — mirror `ProductWireFields` field-for-field
// ---------------------------------------------------------------------------

export interface GqlUserError {
  code: string;
  field: string | null;
  message: string;
}

export interface GqlTaxonomyPathNode {
  id: string;
  labelEn: string;
  labelZh: string;
  depth: number;
}

export interface GqlProductNote {
  id: string;
  audience: string;
  body: string;
  createdAt: string;
}

export interface GqlPackagingLevel {
  levelCode: string;
  gtin: string | null;
}

export interface GqlCustomerPartNumber {
  id: string;
  customerId: number;
  customerName: string | null;
  partNumber: string;
  description: string | null;
  isAuthorized: boolean | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
}

export interface GqlProductImage {
  slot: number;
  url: string;
}

export interface GqlFitmentYearRange {
  fromYear: number | null;
  toYear: number | null;
  variant: string | null;
}

export interface GqlFitment {
  id: string;
  make: string;
  model: string | null;
  variant: string | null;
  yearRanges: GqlFitmentYearRange[];
}

export interface GqlProductVendorLine {
  id: string;
  productId: string;
  vendorId: number;
  vendorName: string | null;
  vendorSku: string | null;
  unitCost: string | null;
  currencyCode: string | null;
  isPrimary: boolean;
  leadTimeDays: number | null;
  lastPurchasePrice: string | null;
  nextPurchasePrice: string | null;
  moq: number | null;
  isActive: boolean;
}

export interface GqlAccountingRefs {
  incomeAccountRef: string | null;
  cogsAccountRef: string | null;
  assetAccountRef: string | null;
  deferredRevenueAccountRef: string | null;
  dropshipExpenseAccountRef: string | null;
  gainLossAccountRef: string | null;
  priceVarianceAccountRef: string | null;
  quantityVarianceAccountRef: string | null;
  exchangeRateVarianceAccountRef: string | null;
  vendorBillPriceVarianceAccountRef: string | null;
  vendorBillQuantityVarianceAccountRef: string | null;
  vendorBillExchangeRateVarianceAccountRef: string | null;
  vendorReturnVarianceAccountRef: string | null;
  customerReturnVarianceAccountRef: string | null;
  intercompanyIncomeAccountRef: string | null;
  intercompanyCogsAccountRef: string | null;
  taxScheduleRef: string | null;
}

export interface GqlReplenishmentPlanning {
  reorderPointQty: number | null;
  preferredStockLevelQty: number | null;
  reorderMultipleQty: number | null;
  leadTimeDays: number | null;
  safetyStockDays: number | null;
  expectedDemandChangePct: string | null;
  seasonalDemand: boolean;
  preOrder: boolean;
  doNotOrder: boolean;
}

/** `ProductWireFields`, field-for-field. */
export interface GqlProductWire {
  id: string;
  productId: string;
  internalProductId: string;
  sku: string;
  scn: string | null;
  isShared: boolean | null;
  nameEn: string;
  nameZh: string | null;
  subsidiaryId: string | null;
  includeChildren: boolean;
  categoryId: string;
  secondaryCategoryIds: string[];
  taxonomyPath: GqlTaxonomyPathNode[];
  unitsTypeId: string;
  uom: string;
  purchaseUom: string;
  salesUom: string;
  customerDescription: string | null;
  vendorDescription: string | null;
  customsDescription: string | null;
  purchaseOrderNote: string | null;
  damageStockNotes: string | null;
  itemComment: string | null;
  notes: GqlProductNote[];
  netSuiteItemId: string | null;
  netSuiteSyncedAt: string | null;
  oemNumber: string | null;
  mpn: string | null;
  packagingLevels: GqlPackagingLevel[];
  customerPartNumbers: GqlCustomerPartNumber[];
  images: GqlProductImage[];
  fitment: GqlFitment[];
  vendorLines: GqlProductVendorLine[];
  variant: string | null;
  extractYear: number | null;
  manufacturer: string | null;
  countryOfManufacture: string | null;
  faSubCategory: string | null;
  ageGroup: string | null;
  ageStartYear: number | null;
  ageEndYear: number | null;
  lengthCm: string | null;
  widthCm: string | null;
  heightCm: string | null;
  weight: string | null;
  weightEntryUnit: string | null;
  costingMethod: string | null;
  basePrice: string | null;
  currencyCode: string | null;
  minQty: number | null;
  minQtyUnit: string | null;
  maxQty: number | null;
  maxQtyUnit: string | null;
  itemDefinedCost: string | null;
  itemDefinedCostUnit: string | null;
  accountingRefs: GqlAccountingRefs | null;
  replenishmentPlanning: GqlReplenishmentPlanning | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProductWireListResult {
  products: {
    totalCount: number;
    pageInfo: { hasNextPage: boolean; endCursor: string | null };
    edges: { node: GqlProductWire }[];
  };
}

export interface ProductWireListVariables {
  first: number;
  after?: string | null;
  filter?: {
    search?: string | null;
    categoryId?: string | null;
    isActive?: boolean | null;
    hasScn?: boolean | null;
    readiness?: 'READY' | 'NOT_READY' | null;
  } | null;
  sort?: { field: string; direction: 'ASC' | 'DESC' } | null;
  [key: string]: unknown;
}

export interface ProductWireDetailResult {
  product: GqlProductWire | null;
}

export interface ProductWritePayload {
  userErrors: GqlUserError[];
  product: GqlProductWire | null;
}

export interface ProductWireCreateResult {
  productCreate: ProductWritePayload;
}

export interface ProductWireUpdateResult {
  productUpdate: ProductWritePayload;
}

export interface ProductWireSetActiveResult {
  productSetActive: ProductWritePayload;
}

export interface ProductSkuAvailabilityResult {
  productSkuAvailability: {
    available: boolean;
    reason: string | null;
    conflict: {
      productId: string;
      internalProductId: string;
      sku: string;
      nameEn: string;
      isActive: boolean;
    } | null;
  };
}
